import { useEffect, useMemo, useState } from 'react'
import { NetworkStatus, useApolloClient, useQuery } from '@apollo/client'

import useFilters, { toSitesUnscopeVariables, useSort } from './store'
import { OPERATION_SEARCH_SITES_UNSCOPED } from './operations-gql/queries'
import { SITES_LIST_QUERIES, SITES_PAGE_LIMIT } from './constants'
import { SiteUnscope } from './types'

type SearchSitesUnscopeData = {
  sites: SiteUnscope[]
}

const LOADING_STATUSES = [NetworkStatus.loading, NetworkStatus.setVariables, NetworkStatus.refetch]

const useSitesUnscope = () => {
  const [filters] = useFilters()
  const [sort] = useSort()
  const [hasNextPage, setHasNextPage] = useState(true)
  const client = useApolloClient()

  const variables = useMemo(() => ({
    ...toSitesUnscopeVariables(filters),
    offset: 0,
    limit: SITES_PAGE_LIMIT,
    sort_by: sort.by,
    sort_direction: sort.direction
  }), [filters, sort.by, sort.direction])

  const { data, error, fetchMore, networkStatus } = useQuery<SearchSitesUnscopeData>(
    OPERATION_SEARCH_SITES_UNSCOPED,
    {
      variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only'
    }
  )

  useEffect(() => {
    setHasNextPage(true)
  }, [variables])

  const sites = useMemo(() => data?.sites ?? [], [data])
  const loading = LOADING_STATUSES.includes(networkStatus)
  const loadingNext = networkStatus === NetworkStatus.fetchMore

  const fetchNextPage = async () => {
    const { data: nextPageData } = await fetchMore({
      variables: { offset: sites.length },
      updateQuery: (previousData, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousData
        return { sites: [...previousData.sites, ...fetchMoreResult.sites] }
      }
    })

    if (nextPageData.sites.length < SITES_PAGE_LIMIT) {
      setHasNextPage(false)
    }
  }

  const refresh = async () => {
    setHasNextPage(true)
    await client.refetchQueries({ include: SITES_LIST_QUERIES })
  }

  return {
    sites,
    error,
    loading,
    loadingNext,
    shouldFetchNextPage: !loading && !loadingNext && hasNextPage && sites.length >= SITES_PAGE_LIMIT,
    fetchNextPage,
    refresh
  }
}

export type UseSitesUnscope = ReturnType<typeof useSitesUnscope>

export default useSitesUnscope
